import { CommunicationTypes } from "constants/communication-activity";
import { Lang } from "constants/flags";

import { ParticipantType as RecipientType } from "utils/types/employee-journey";

export enum ParticipantType {
	BUDDY = "buddy",
	CUSTOM = "custom",
}

export type EmployeeJourney = {
	_id: string;
	name: string;
	description: string;
	organization: string;
	type: string;
	status: EmployeeJourneyStatus;

	totalAssignedEmployees: number;
	totalCompleteEmployees: number;
	completionPercentage: number;

	createdBy: string;
	updatedBy: {
		email: string;
	};
	createdAt: string;
	updatedAt: string;
};

export enum EmployeeJourneyStatus {
	DRAFT = "draft",
	PUBLISHED = "published",
	ARCHIVED = "archived",
}

export enum JourneyActivityType {
	MEETING = "meeting",
	UPLOAD_FILE = "upload_file",
	STORY = "story",
	COMMUNICATION = "communication",
	SETUP_GOAL = "setup_goal",
	QUESTION = "questionnaire",
	TASK = "task",
}

export type JourneyActivityBase = {
	_id: string;
	routeId: string;

	name: string;
	description?: string;
	relativeDueDate: Date;
	relativeEndDate: Date;

	dueRelativeDateTemplate: string;
	lockedUntilRelativeDateTemplate: string;
	date?: Date;

	// values for view
	eventTime?: string;
	duration?: number;
	dueDate?: string;
	lockedDate?: string;

	file?: File | Blob;
	fileType?: string;

	dependencies?: string[];

	type: JourneyActivityType;
};

export interface StoryActivity extends JourneyActivityBase {
	type: JourneyActivityType.STORY;
	roomTemplateId: string;
	maxNoUsers: number;
}

export interface MeetingActivity extends JourneyActivityBase {
	type: JourneyActivityType.MEETING;
	startTime: string;
	endTime: string;
	participants?: { email: string; name: string }[];
}

export interface UploadFileActivity extends JourneyActivityBase {
	type: JourneyActivityType.UPLOAD_FILE;
	fileType: string;
	filePath: string;
	file: any;
	maxSize: number;
	maxFileCount: number;
}

export interface GoalActivity extends JourneyActivityBase {
	type: JourneyActivityType.SETUP_GOAL;
	needApproval: boolean;
}

export interface QuestionActivity extends JourneyActivityBase {
	type: JourneyActivityType.QUESTION;
	questionnaireId: {
		questions: any[];
	};
	ruleset: {
		questionnaireId: string;
		type: JourneyActivityType.QUESTION;
		sendEmail: boolean;
		recipients: {
			name?: string;
			email?: string;
			participantType: ParticipantType;
		}[];
	};
	recipients: {
		name?: string;
		email?: string;
		participantType: ParticipantType;
	}[];
	sendEmail: boolean;
}

export interface TaskActivity extends JourneyActivityBase {
	placeholder: string;
	taskDate: string; // Same as relative due date
	taskTime: string; // HH:mm
	journeyRouteTemplateId: string;
	organization: string;
	recipients: {
		name?: string;
		email?: string;
		participantType: RecipientType;
	}[];
	type: JourneyActivityType.TASK;
}

interface CommunicationValidationSchema {
	subject: string;
	message: string;
	communicationType: CommunicationTypes;
	time: string;
	placeholder: string;
	recipients: { name?: string; email?: string; participantType: ParticipantType }[];
	type: JourneyActivityType.COMMUNICATION;
}

export interface CommunicationActivity extends JourneyActivityBase, CommunicationValidationSchema {
	type: JourneyActivityType.COMMUNICATION;
}

export type JourneyActivity =
	| StoryActivity
	| MeetingActivity
	| UploadFileActivity
	| CommunicationActivity
	| GoalActivity
	| QuestionActivity
	| TaskActivity;

export enum JourneyTemplatStatus {
	DRAFT = "draft",
	PUBLISHED = "published",
	ARCHIVED = "archived",
}

export type JourneyRoute = {
	_id: string | null;
	name: string;
	description: string;
	icon?: string;
	iconUrl?: string;
	type: string;
	activities: JourneyActivity[];
};

export type JourneyTemplate = {
	_id: string;
	name: string;
	language: string;
	description: string;
	logo?: string;
	status: JourneyTemplatStatus;
	routes: JourneyRoute[];
};

interface RouteIds extends Omit<JourneyTemplate, "language" | "logo" | "status"> {
	createdBy: string;
	updatedBy: string;
	journeyTemplateId: string;
	elements: JourneyActivity[];
}

export interface SuperAdminTemplate extends Omit<JourneyTemplate, "description" | "logo" | "routes" | "language"> {
	createdBy: string;
	updatedBy: string;
	type: "template";
	language: Lang;
	routesIds: RouteIds[];
}
